import * as THREE from 'three';
import {
  MESH_NAME_SWORD,
  SWORD_BLADE_COLOR,
  SWORD_BLADE_LENGTH_M,
  SWORD_BLADE_METALNESS,
  SWORD_BLADE_ROUGHNESS,
  SWORD_BLADE_THICKNESS_BASE_M,
  SWORD_BLADE_THICKNESS_TIP_M,
  SWORD_BLADE_WIDTH_BASE_M,
  SWORD_BLADE_WIDTH_TIP_M,
  SWORD_GRIP_LENGTH_M,
  SWORD_GRIP_RADIUS_M,
  SWORD_HILT_COLOR,
  SWORD_HILT_ROUGHNESS,
  SWORD_MODEL_SRC_AXIS_X,
  SWORD_MODEL_SRC_AXIS_Y,
  SWORD_MODEL_SRC_GUARD_Z,
  SWORD_MODEL_SRC_TIP_Z,
  SWORD_MODEL_URL,
  SWORD_POMMEL_RADIUS_M,
  SWORD_QUILLON_THICKNESS_M,
  SWORD_QUILLON_WIDTH_M,
} from '../config/constants';

/** Children with this name are placeholder parts, removed once the GLB swaps in. */
const PART_PRIMITIVE = 'sword-primitive';

/**
 * Sword origin = grip center at (0,0,0) — aligned with the controller (user's hand).
 * Pommel sits behind the hand at +Z, quillon in front at -gripLen/2, blade extends along -Z
 * (matches WebXR controller forward).
 * Edge direction (filo dritto) = +X local (spine→edge, knuckles in saber grip).
 * Blade flat lies in the X-Z plane (flat normal = ±Y).
 * Quillon long axis = ±X (parallel to edge, in the plane of the flat — cruciform hilt).
 *
 * Builds primitive placeholders synchronously; call `upgradeSwordModel` to swap
 * in the real GLB once loaded (its asymmetric knuckle-bow hilt sits on the +X
 * edge side, making the orientation readable without a dedicated marker).
 */
export function createSword(): THREE.Group {
  const group = new THREE.Group();
  group.name = MESH_NAME_SWORD;

  const bladeMat = new THREE.MeshStandardMaterial({
    color: SWORD_BLADE_COLOR,
    metalness: SWORD_BLADE_METALNESS,
    roughness: SWORD_BLADE_ROUGHNESS,
  });
  const hiltMat = new THREE.MeshStandardMaterial({
    color: SWORD_HILT_COLOR,
    roughness: SWORD_HILT_ROUGHNESS,
  });

  const blade = new THREE.Mesh(createTaperedBladeGeometry(), bladeMat);
  blade.name = PART_PRIMITIVE;
  blade.position.z = -(SWORD_GRIP_LENGTH_M / 2 + SWORD_BLADE_LENGTH_M / 2);
  group.add(blade);

  const quillon = new THREE.Mesh(
    new THREE.BoxGeometry(
      SWORD_QUILLON_WIDTH_M,
      SWORD_QUILLON_THICKNESS_M,
      SWORD_QUILLON_THICKNESS_M,
    ),
    hiltMat,
  );
  quillon.name = PART_PRIMITIVE;
  quillon.position.z = -SWORD_GRIP_LENGTH_M / 2;
  group.add(quillon);

  const grip = new THREE.Mesh(
    new THREE.CylinderGeometry(SWORD_GRIP_RADIUS_M, SWORD_GRIP_RADIUS_M, SWORD_GRIP_LENGTH_M, 6),
    hiltMat,
  );
  grip.name = PART_PRIMITIVE;
  grip.rotation.x = -Math.PI / 2;
  group.add(grip);

  const pommel = new THREE.Mesh(new THREE.SphereGeometry(SWORD_POMMEL_RADIUS_M, 8, 6), hiltMat);
  pommel.name = PART_PRIMITIVE;
  pommel.position.z = SWORD_GRIP_LENGTH_M / 2;
  group.add(pommel);

  return group;
}

let swordModelPromise: Promise<THREE.Group> | null = null;

/**
 * Start (or reuse) the async load of the sidesword GLB. Resolves to a single
 * shared wrapper group already aligned to the sword local frame. A failed load
 * clears the cache so the next call retries; callers handle rejection.
 */
export function preloadSwordModel(): Promise<THREE.Group> {
  swordModelPromise ??= loadSwordModel().catch((err: unknown) => {
    swordModelPromise = null;
    throw err;
  });
  return swordModelPromise;
}

async function loadSwordModel(): Promise<THREE.Group> {
  // Dynamic import keeps GLTFLoader out of the main bundle (pairs with #7).
  const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
  const gltf = await new GLTFLoader().loadAsync(SWORD_MODEL_URL);
  return alignSwordModel(gltf.scene);
}

/**
 * Swap the primitive placeholder parts for the real GLB once it loads.
 * On load failure the primitives stay — same pattern as the floor textures.
 * The model is a shared singleton: adding it re-parents it away from any
 * previous session's (already discarded) sword group.
 */
export async function upgradeSwordModel(group: THREE.Group): Promise<void> {
  let model: THREE.Group;
  try {
    model = await preloadSwordModel();
  } catch (err) {
    console.warn('[sword] model load failed, keeping primitive sword', err);
    return;
  }
  for (const child of group.children.filter((c) => c.name === PART_PRIMITIVE)) {
    group.remove(child);
    const mesh = child as THREE.Mesh;
    mesh.geometry.dispose();
    (mesh.material as THREE.Material).dispose();
  }
  group.add(model);
}

/**
 * Wrap the loaded GLB scene in a group mapping its source space (mm; blade
 * along +Z, pommel −Z, knuckle bow / true edge −X, blade centerline at
 * SRC_AXIS_X/Y) onto the sword local frame: π around Y flips the blade to −Z
 * and brings the knuckle bow to +X (the edge side), a uniform scale makes
 * guard→tip equal SWORD_BLADE_LENGTH_M, and the offset puts the guard center
 * at −grip/2 on the blade axis. Tip and guard then match hit detection exactly;
 * the visual grip may deviate from the constants by a few mm (accepted — the
 * hand covers it).
 */
function alignSwordModel(model: THREE.Group): THREE.Group {
  const wrapper = new THREE.Group();
  const scale = SWORD_BLADE_LENGTH_M / (SWORD_MODEL_SRC_TIP_Z - SWORD_MODEL_SRC_GUARD_Z);
  wrapper.rotation.y = Math.PI;
  wrapper.scale.setScalar(scale);
  wrapper.position.set(
    SWORD_MODEL_SRC_AXIS_X * scale,
    -SWORD_MODEL_SRC_AXIS_Y * scale,
    -SWORD_GRIP_LENGTH_M / 2 + SWORD_MODEL_SRC_GUARD_Z * scale,
  );
  wrapper.add(model);
  return wrapper;
}

/**
 * Flat blade box that tapers from base to tip on both axes.
 * X = spine→edge (wider), Y = flat normal (thinner), Z = blade length.
 * Box is centered on local origin; caller positions it along -Z.
 */
function createTaperedBladeGeometry(): THREE.BufferGeometry {
  const geom = new THREE.BoxGeometry(
    SWORD_BLADE_WIDTH_BASE_M,
    SWORD_BLADE_THICKNESS_BASE_M,
    SWORD_BLADE_LENGTH_M,
  );
  const widthRatio = SWORD_BLADE_WIDTH_TIP_M / SWORD_BLADE_WIDTH_BASE_M;
  const thicknessRatio = SWORD_BLADE_THICKNESS_TIP_M / SWORD_BLADE_THICKNESS_BASE_M;
  const positions = geom.attributes['position'];
  if (!positions) throw new Error('BoxGeometry missing position attribute');
  for (let i = 0; i < positions.count; i++) {
    if (positions.getZ(i) < 0) {
      positions.setX(i, positions.getX(i) * widthRatio);
      positions.setY(i, positions.getY(i) * thicknessRatio);
    }
  }
  positions.needsUpdate = true;
  geom.computeVertexNormals();
  return geom;
}
